import React, { useState, useMemo } from "react";
import {
  Trophy,
  Users,
  BarChart2,
  Calendar,
  MapPin,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Filter,
  TrendingUp,
  Award,
  Search,
  FileText,
  Info,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import mathcountsData from "../../data/mathcounts.json";

const MathcountsSection = () => {
  const { year, stateCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("yearly");
  const [selectedYear, setSelectedYear] = useState(
    mathcountsData[0]?.year || 2026,
  );
  const [selectedState, setSelectedState] = useState("CA");
  const [selectedGrade, setSelectedGrade] = useState("All");
  const [selectedGender, setSelectedGender] = useState("All");
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");
  const [stateYearFilter, setStateYearFilter] = useState("All");
  const [stateGenderFilter, setStateGenderFilter] = useState("All");
  const [sortConfig, setSortConfig] = useState({
    key: "rank",
    direction: "asc",
  });
  const [championsOnly, setChampionsOnly] = useState(false);
  const [championsViewMode, setChampionsViewMode] = useState("count"); // 'count' | 'detail'
  const [champTableSort, setChampTableSort] = useState({
    key: "total",
    dir: "desc",
  });
  const [threeTimeSort, setThreeTimeSort] = useState({
    key: "years",
    dir: "desc",
  });
  const [threeTimeGenderFilter, setThreeTimeGenderFilter] = useState("All");
  const [threeTimeCountdownOnly, setThreeTimeCountdownOnly] = useState(false);
  const [hallOfFameChampSort, setHallOfFameChampSort] = useState({
    key: "wins",
    dir: "desc",
  });
  const [hallOfFameChampGenderFilter, setHallOfFameChampGenderFilter] = useState("All");

  const handleHallOfFameChampSort = (key) => {
    setHallOfFameChampSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc",
    }));
  };
  const [stateStudentsSort, setStateStudentsSort] = useState({
    key: "year",
    dir: "desc",
  });

  const handleStateStudentsSort = (key) => {
    setStateStudentsSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc",
    }));
  };

  const handleThreeTimeSort = (key) => {
    setThreeTimeSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc",
    }));
  };

  const handleChampSort = (key) => {
    setChampTableSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc",
    }));
  };

  const navigateToSearch = (name) => {
    setGlobalSearchQuery(name);
    setActiveTab("search");
    navigate("/mathcounts/search");
  };

  const allYears = useMemo(
    () => mathcountsData.map((d) => d.year).sort((a, b) => a - b),
    [],
  );
  const [statsFromYear, setStatsFromYear] = useState(null);
  const [statsToYear, setStatsToYear] = useState(null);

  // Sync state with URL params
  React.useEffect(() => {
    if (location.pathname.includes("/statistics")) {
      setActiveTab("statistics");
    } else if (location.pathname.includes("/halloffame")) {
      setActiveTab("halloffame");
    } else if (location.pathname.includes("/search")) {
      setActiveTab("search");
    } else if (stateCode) {
      setActiveTab("state");
      setSelectedState(stateCode.toUpperCase());
    } else if (year) {
      setActiveTab("yearly");
      setSelectedYear(parseInt(year));
    } else {
      setActiveTab("yearly");
    }
  }, [year, stateCode, location.pathname]);

  const handleTabChange = (tabId) => {
    if (tabId === "yearly") navigate(`/mathcounts/year/${selectedYear}`);
    else if (tabId === "state") navigate(`/mathcounts/state/${selectedState}`);
    else if (tabId === "statistics") navigate("/mathcounts/statistics");
    else if (tabId === "halloffame") navigate("/mathcounts/halloffame");
    else if (tabId === "search") navigate("/mathcounts/search");
  };

  const handleYearChange = (newYear) => {
    setSelectedYear(newYear);
    navigate(`/mathcounts/year/${newYear}`);
  };

  const handleStateChange = (newState) => {
    setSelectedState(newState);
    setStateYearFilter("All");
    navigate(`/mathcounts/state/${newState}`);
  };

  const stateNameMap = {
    AL: "Alabama",
    AK: "Alaska",
    AZ: "Arizona",
    AR: "Arkansas",
    CA: "California",
    CO: "Colorado",
    CT: "Connecticut",
    DE: "Delaware",
    FL: "Florida",
    GA: "Georgia",
    HI: "Hawaii",
    ID: "Idaho",
    IL: "Illinois",
    IN: "Indiana",
    IA: "Iowa",
    KS: "Kansas",
    KY: "Kentucky",
    LA: "Louisiana",
    ME: "Maine",
    MD: "Maryland",
    MA: "Massachusetts",
    MI: "Michigan",
    MN: "Minnesota",
    MS: "Mississippi",
    MO: "Missouri",
    MT: "Montana",
    NE: "Nebraska",
    NV: "Nevada",
    NH: "New Hampshire",
    NJ: "New Jersey",
    NM: "New Mexico",
    NY: "New York",
    NC: "North Carolina",
    ND: "North Dakota",
    OH: "Ohio",
    OK: "Oklahoma",
    OR: "Oregon",
    PA: "Pennsylvania",
    RI: "Rhode Island",
    SC: "South Carolina",
    SD: "South Dakota",
    TN: "Tennessee",
    TX: "Texas",
    UT: "Utah",
    VT: "Vermont",
    VA: "Virginia",
    WA: "Washington",
    WV: "West Virginia",
    WI: "Wisconsin",
    WY: "Wyoming",
    DC: "District of Columbia",
    PR: "Puerto Rico",
    GU: "Guam",
    VI: "Virgin Islands",
    SS: "State Department",
  };

  const reverseStateMap = useMemo(() => {
    const reversed = {};
    Object.entries(stateNameMap).forEach(([abbr, full]) => {
      reversed[full] = abbr;
    });
    return reversed;
  }, []);

  // --- Aggregate Stats Data ---
  const aggregateStats = useMemo(() => {
    const stateAppearances = {};
    const stateWeights = {};
    const genderTotals = { Male: 0, Female: 0, Unknown: 0 };
    let totalStudentsCount = 0;

    const getWeight = (rank) => {
      if (rank === "1") return 56;
      if (rank === "2") return 55;
      if (rank === "S") return 53.5;
      if (rank === "Q") return 50.5;
      if (rank === "C" || rank === "P") return 46.5;
      const numRank = parseInt(rank);
      if (!isNaN(numRank)) return 56 - numRank + 1;
      return 0;
    };

    const filteredData = mathcountsData.filter((d) => {
      const from = statsFromYear ?? allYears[0];
      const to = statsToYear ?? allYears[allYears.length - 1];
      return d.year >= from && d.year <= to;
    });

    filteredData.forEach((year) => {
      year.students.forEach((s) => {
        stateAppearances[s.state] = (stateAppearances[s.state] || 0) + 1;
        stateWeights[s.state] =
          (stateWeights[s.state] || 0) + getWeight(s.rank);
        genderTotals[s.gender || "Unknown"]++;
        totalStudentsCount++;
      });
    }); // end filteredData.forEach

    const top15States = Object.entries(stateAppearances)
      .map(([state, count]) => ({
        state,
        fullName: stateNameMap[state] || state,
        count,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);

    const weightedStates = Object.entries(stateWeights)
      .map(([state, weight]) => ({
        state,
        fullName: stateNameMap[state] || state,
        weight: parseFloat(weight.toFixed(1)),
      }))
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 15);

    return {
      top15States,
      weightedStates,
      genderTotals,
      totalStudentsCount,
    };
  }, [statsFromYear, statsToYear, allYears]);

  const teamRankingsMatrix = useMemo(() => {
    const years = mathcountsData.map((d) => d.year).sort((a, b) => b - a);
    const stateMap = {};

    mathcountsData.forEach((yearData) => {
      if (yearData.teams) {
        yearData.teams.forEach((team) => {
          if (!stateMap[team.name]) {
            stateMap[team.name] = {};
          }
          stateMap[team.name][yearData.year] = parseInt(team.rank, 10);
        });
      }
    });

    const states = Object.keys(stateMap).sort();

    return { years, states, stateMap };
  }, []);

  const individualChampionsMatrix = useMemo(() => {
    const stateMap = {};

    const add = (state, category, year, name) => {
      if (!state) return;
      if (!stateMap[state])
        stateMap[state] = {
          cdChamp: [],
          cdRunnerUp: [],
          wrChamp: [],
          wrRunnerUp: [],
        };
      stateMap[state][category].push({ year, name });
    };

    mathcountsData.forEach((yearData) => {
      if (!yearData.noCountdown) {
        const cdChamp = yearData.students.find((s) => s.rank === "1");
        const cdRunnerUp = yearData.students.find((s) => s.rank === "2");
        if (cdChamp) add(cdChamp.state, "cdChamp", yearData.year, cdChamp.name);
        if (cdRunnerUp)
          add(cdRunnerUp.state, "cdRunnerUp", yearData.year, cdRunnerUp.name);
      }
      if (yearData.writtenChampion?.state)
        add(
          yearData.writtenChampion.state,
          "wrChamp",
          yearData.year,
          yearData.writtenChampion.name,
        );
      if (yearData.writtenRunnerUp?.state)
        add(
          yearData.writtenRunnerUp.state,
          "wrRunnerUp",
          yearData.year,
          yearData.writtenRunnerUp.name,
        );
    });

    return Object.entries(stateMap)
      .map(([state, cats]) => ({
        state,
        ...cats,
        total:
          cats.cdChamp.length +
          cats.cdRunnerUp.length +
          cats.wrChamp.length +
          cats.wrRunnerUp.length,
      }))
      .sort((a, b) => b.total - a.total);
  }, []);

  const threeTimeQualifiers = useMemo(() => {
    const studentMap = {};

    mathcountsData.forEach((yearData) => {
      yearData.students.forEach((student) => {
        const key = `${student.name}|${student.state}`;
        if (!studentMap[key]) {
          studentMap[key] = {
            name: student.name,
            state: student.state,
            gender: student.gender,
            years: [],
            ranks: [],
          };
        }
        studentMap[key].years.push(yearData.year);
        studentMap[key].ranks.push(student.rank);
      });
    });

    const isCountdownRank = (rank) => ["1", "2", "S", "Q", "C", "P"].includes(rank);

    const hasConsecutiveThree = (years) => {
      if (years.length < 3) return false;
      const sortedYears = [...years].sort((a, b) => a - b);
      for (let i = 0; i <= sortedYears.length - 3; i++) {
        if (
          sortedYears[i + 1] === sortedYears[i] + 1 &&
          sortedYears[i + 2] === sortedYears[i] + 2
        ) {
          return true;
        }
      }
      return false;
    };

    const hasConsecutiveThreeCountdown = (student) => {
      const records = student.years.map((year, idx) => ({
        year,
        rank: student.ranks[idx],
      })).sort((a, b) => a.year - b.year);

      if (records.length < 3) return false;
      for (let i = 0; i <= records.length - 3; i++) {
        const r1 = records[i];
        const r2 = records[i + 1];
        const r3 = records[i + 2];
        if (
          r2.year === r1.year + 1 &&
          r3.year === r1.year + 2 &&
          isCountdownRank(r1.rank) &&
          isCountdownRank(r2.rank) &&
          isCountdownRank(r3.rank)
        ) {
          return true;
        }
      }
      return false;
    };

    return Object.values(studentMap)
      .filter((s) => {
        const hasConsecutive = hasConsecutiveThree(s.years);
        const matchesGender =
          threeTimeGenderFilter === "All" || s.gender === threeTimeGenderFilter;
        const matchesCountdown =
          !threeTimeCountdownOnly || hasConsecutiveThreeCountdown(s);
        return hasConsecutive && matchesGender && matchesCountdown;
      })
      .sort((a, b) => {
        const { key, dir } = threeTimeSort;
        let aVal = a[key];
        let bVal = b[key];

        if (key === "years") {
          aVal = a.years.length;
          bVal = b.years.length;
        }

        if (aVal < bVal) return dir === "asc" ? -1 : 1;
        if (aVal > bVal) return dir === "asc" ? 1 : -1;
        return a.name.localeCompare(b.name);
      });
  }, [threeTimeSort, threeTimeGenderFilter, threeTimeCountdownOnly]);

  const nationalChampions = useMemo(() => {
    const championMap = {};

    mathcountsData.forEach((yearData) => {
      const champion = yearData.students.find((s) => parseInt(s.rank, 10) === 1);
      if (champion) {
        const key = `${champion.name}|${champion.state}`;
        if (!championMap[key]) {
          championMap[key] = {
            name: champion.name,
            state: champion.state,
            gender: champion.gender,
            years: [],
            wins: 0,
          };
        }
        championMap[key].years.push(yearData.year);
        championMap[key].wins += 1;
      }
    });

    return Object.values(championMap)
      .filter((s) => {
        return (
          hallOfFameChampGenderFilter === "All" ||
          s.gender === hallOfFameChampGenderFilter
        );
      })
      .sort((a, b) => {
        const { key, dir } = hallOfFameChampSort;
        let aVal = a[key];
        let bVal = b[key];

        if (key === "years") {
          aVal = a.years.length;
          bVal = b.years.length;
        }

        if (aVal < bVal) return dir === "asc" ? -1 : 1;
        if (aVal > bVal) return dir === "asc" ? 1 : -1;

        if (b.wins !== a.wins) return b.wins - a.wins;
        return a.name.localeCompare(b.name);
      });
  }, [hallOfFameChampSort, hallOfFameChampGenderFilter]);

  const mostDecorated = useMemo(() => {
    const championMap = {};
    mathcountsData.forEach((yearData) => {
      const champion = yearData.students.find((s) => parseInt(s.rank, 10) === 1);
      if (champion) {
        const key = `${champion.name}|${champion.state}`;
        if (!championMap[key]) {
          championMap[key] = {
            name: champion.name,
            state: champion.state,
            gender: champion.gender,
            years: [],
            wins: 0,
          };
        }
        championMap[key].years.push(yearData.year);
        championMap[key].wins += 1;
      }
    });
    const sorted = Object.values(championMap).sort((a, b) => b.wins - a.wins);
    return sorted.length > 0 ? sorted[0] : null;
  }, []);

  const searchResults = useMemo(() => {
    if (globalSearchQuery.trim().length < 2) return [];
    const query = globalSearchQuery.toLowerCase();
    const results = [];

    mathcountsData.forEach((year) => {
      year.students.forEach((student) => {
        if (student.name.toLowerCase().includes(query)) {
          results.push({ ...student, year: year.year });
        }
      });
    });

    return results.sort((a, b) => {
      if (b.year !== a.year) return b.year - a.year;
      const rankOrder = { 1: 1, 2: 2, S: 3, Q: 5, C: 9, P: 10 };
      const getRankVal = (r) => rankOrder[r] || parseInt(r) || 999;
      return getRankVal(a.rank) - getRankVal(b.rank);
    });
  }, [globalSearchQuery]);

  // --- Yearly Results Data ---
  const currentYearData = useMemo(() => {
    return mathcountsData.find((d) => d.year === selectedYear);
  }, [selectedYear]);

  const filteredStudents = useMemo(() => {
    if (!currentYearData) return [];
    let students = [...currentYearData.students];

    if (selectedGrade !== "All") {
      students = students.filter((s) => s.grade === parseInt(selectedGrade));
    }

    if (selectedGender !== "All") {
      students = students.filter((s) => s.gender === selectedGender);
    }

    students.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      const rankOrder = { 1: 1, 2: 2, S: 3, Q: 5, C: 9, P: 9 };
      if (sortConfig.key === "rank") {
        aVal = rankOrder[a.rank] || parseInt(a.rank) || 999;
        bVal = rankOrder[b.rank] || parseInt(b.rank) || 999;
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return students;
  }, [currentYearData, selectedGrade, selectedGender, sortConfig]);

  const yearlyStats = useMemo(() => {
    if (!currentYearData) return null;
    if (currentYearData.isPartial) {
      return {
        totalStudents: "1 Champion",
        topState: currentYearData.students[0]?.state || "N/A",
        genderRatio: "N/A",
      };
    }
    const students = currentYearData.students;
    const stateCounts = {};
    let femaleCount = 0;

    students.forEach((s) => {
      stateCounts[s.state] = (stateCounts[s.state] || 0) + 1;
      if (s.gender === "Female") femaleCount++;
    });

    const maxCount = Math.max(...Object.values(stateCounts));
    const topStates = Object.entries(stateCounts)
      .filter(([state, count]) => count === maxCount)
      .map(([state]) => state);

    return {
      totalStudents: students.length,
      topState:
        topStates.length > 0 ? `${topStates.join(", ")} (${maxCount})` : "N/A",
      genderRatio: `${Math.round((femaleCount / students.length) * 100)}% Female`,
    };
  }, [currentYearData]);

  // --- State Results Data ---
  const allStates = useMemo(() => {
    const states = new Set();
    mathcountsData.forEach((year) => {
      year.students.forEach((s) => states.add(s.state));
    });
    return Array.from(states).sort();
  }, []);

  const stateData = useMemo(() => {
    const studentHistory = [];
    const teamHistory = [];
    const fullStateName = stateNameMap[selectedState] || selectedState;

    mathcountsData.forEach((year) => {
      year.students.forEach((s) => {
        if (s.state === selectedState) {
          studentHistory.push({ ...s, year: year.year });
        }
      });

      const team = year.teams.find((t) => t.name === fullStateName);
      if (team) {
        teamHistory.push({ year: year.year, rank: team.rank });
      }
    });

    studentHistory.sort((a, b) => {
      if (b.year !== a.year) return b.year - a.year;
      const rankOrder = { 1: 1, 2: 2, S: 3, Q: 5, C: 9, P: 10 };
      const getRankVal = (r) => rankOrder[r] || parseInt(r) || 999;
      return getRankVal(a.rank) - getRankVal(b.rank);
    });

    const uniqueStudents = new Set(studentHistory.map((s) => s.name));
    
    // Team ranks & best team rank years
    const teamRanks = teamHistory
      .map((t) => ({ year: t.year, rank: parseInt(t.rank, 10) }))
      .filter((t) => !isNaN(t.rank));
    const bestTeamRank = teamRanks.length > 0 ? Math.min(...teamRanks.map(t => t.rank)) : 999;
    const bestTeamRankYears = teamRanks.length > 0 
      ? Array.from(new Set(teamRanks.filter(t => t.rank === bestTeamRank).map(t => t.year))).sort((a, b) => b - a)
      : [];

    // Individual ranks & best individual rank years
    const rankOrder = { 1: 1, 2: 2, S: 3, Q: 5, C: 9, P: 9 };
    const getRankVal = (r) => rankOrder[r] || parseInt(r, 10) || 999;
    const bestRank = studentHistory.length > 0 ? Math.min(...studentHistory.map((s) => getRankVal(s.rank))) : 999;
    const bestRankStudents = studentHistory.filter((s) => getRankVal(s.rank) === bestRank);
    const bestRankYears = Array.from(new Set(bestRankStudents.map((s) => s.year))).sort((a, b) => b - a);

    return {
      students: studentHistory,
      teams: teamHistory,
      totalAppearances: studentHistory.length,
      uniqueStudentsCount: uniqueStudents.size,
      bestTeamRank,
      bestTeamRankYears,
      bestRank,
      bestRankYears,
    };
  }, [selectedState]);

  const availableStateYears = useMemo(() => {
    const years = new Set();
    stateData.students.forEach((s) => years.add(s.year));
    return Array.from(years).sort((a, b) => b - a);
  }, [stateData.students]);

  const filteredStateStudents = useMemo(() => {
    let students = [...stateData.students];
    if (stateYearFilter !== "All") {
      students = students.filter((s) => s.year === parseInt(stateYearFilter));
    }
    if (stateGenderFilter !== "All") {
      students = students.filter((s) => s.gender === stateGenderFilter);
    }

    const { key, dir } = stateStudentsSort;
    students.sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];

      if (key === "rank") {
        const rankOrder = { 1: 1, 2: 2, S: 3, Q: 5, C: 9, P: 10 };
        const getRankVal = (r) => rankOrder[r] || parseInt(r) || 999;
        aVal = getRankVal(a.rank);
        bVal = getRankVal(b.rank);
      }

      if (aVal < bVal) return dir === "asc" ? -1 : 1;
      if (aVal > bVal) return dir === "asc" ? 1 : -1;

      // Secondary sort
      if (key !== "year") {
        if (b.year !== a.year) return b.year - a.year;
      }
      return 0;
    });

    return students;
  }, [
    stateData.students,
    stateYearFilter,
    stateGenderFilter,
    stateStudentsSort,
  ]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column)
      return (
        <ChevronDown className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100" />
      );
    return sortConfig.direction === "asc" ? (
      <ChevronUp className="h-4 w-4 text-blue-600" />
    ) : (
      <ChevronDown className="h-4 w-4 text-blue-600" />
    );
  };

  return (
    <div className="space-y-10 animate-fade-in pb-20">
      <div className="bg-gradient-to-br from-white to-blue-50 rounded-3xl p-8 md:p-12 border border-blue-100 shadow-sm relative overflow-hidden">
        <div className="relative z-10 max-w-4xl">
          <h2 className="text-4xl font-extrabold text-gray-900 mb-6">
            MATHCOUNTS National Statistics
          </h2>
          <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
            <p>
              The MATHCOUNTS National Competition is the absolute pinnacle of
              middle school mathematics in the United States, bringing together
              the top 4 students from every state and territory.
            </p>
            <p>
              Founded in 1983, MATHCOUNTS provides engaging programs that build
              confidence and improve attitudes about math. The national
              competition consists of the intense{" "}
              <span className="font-semibold text-blue-600">Sprint</span> and{" "}
              <span className="font-semibold text-blue-600">Target</span>{" "}
              rounds, the collaborative{" "}
              <span className="font-semibold text-blue-600">Team Round</span>,
              and culminates in the fast-paced, nationally televised{" "}
              <span className="font-semibold text-blue-600">
                Countdown Round
              </span>
              .
            </p>
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="https://www.mathcounts.org"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center"
            >
              Official Website
              <ChevronRight className="h-5 w-5 ml-2" />
            </a>
            <div className="bg-white/80 backdrop-blur px-6 py-3 rounded-xl border border-blue-100 flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              <span className="font-semibold text-gray-700">
                100,000+ Participants Annually
              </span>
            </div>
          </div>
        </div>
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl"></div>
      </div>

      {/* Main Tabs */}
      <div className="flex border-b border-gray-200 overflow-x-auto scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
        <div className="flex min-w-full md:min-w-0">
          {[
            { id: "yearly", label: "Yearly Results", icon: Calendar },
            { id: "state", label: "State Results", icon: MapPin },
            { id: "statistics", label: "Statistics", icon: BarChart2 },
            { id: "halloffame", label: "Hall of Fame", icon: Trophy },
            { id: "search", label: "Search", icon: Search },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-4 md:px-8 py-4 font-bold text-base md:text-lg transition-all relative flex items-center gap-2 flex-shrink-0 ${
                activeTab === tab.id
                  ? "text-blue-600"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              <tab.icon
                className={`h-4 w-4 md:h-5 md:w-5 ${activeTab === tab.id ? "text-blue-600" : "text-gray-400"}`}
              />
              <span className="whitespace-nowrap">{tab.label}</span>
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600 rounded-t-full"></div>
              )}
            </button>
          ))}
        </div>
      </div>

      {activeTab === "yearly" && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <Calendar className="h-5 w-5 text-blue-600 mr-2" />
                Select Year
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-2 max-h-[600px] overflow-y-auto pr-1">
                {mathcountsData.map((d) => (
                  <button
                    key={d.year}
                    onClick={() => handleYearChange(d.year)}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      selectedYear === d.year
                        ? "bg-blue-600 text-white shadow-md"
                        : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {d.year}
                  </button>
                ))}
              </div>
            </div>

            <div className="lg:col-span-3 bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Trophy className="h-6 w-6 text-yellow-500 mr-2" />
                  Competition Leaders: {selectedYear}
                </h3>
                {[
                  2008, 2009, 2010, 2011, 2012, 2013, 2014, 2015, 2016, 2017,
                  2018, 2019, 2021, 2022, 2023, 2024, 2025, 2026,
                ].includes(selectedYear) ? (
                  <a
                    href={`/mathcounts-pdfs/${selectedYear}.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-50 text-blue-600 rounded-xl font-bold text-sm hover:bg-blue-100 transition-colors flex items-center gap-2 self-start sm:self-auto"
                  >
                    <FileText className="h-4 w-4" />
                    View Original PDF
                  </a>
                ) : (
                  <div className="px-4 py-2 bg-gray-50 text-gray-400 rounded-xl font-bold text-sm flex items-center gap-2 self-start sm:self-auto cursor-not-allowed border border-gray-100">
                    <FileText className="h-4 w-4" />
                    Official PDF Not Available
                  </div>
                )}
              </div>

              <div className="space-y-6">
                {currentYearData.isPartial ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-yellow-50 to-white rounded-2xl p-6 border border-yellow-200 relative overflow-hidden group hover:shadow-md transition-all">
                      <p className="text-xs font-black text-yellow-600 uppercase tracking-[0.2em] mb-2">
                        National Individual Champion
                      </p>
                      <p className="text-2xl font-black text-gray-900 mb-1">
                        <button
                          onClick={() => {
                            const w = currentYearData.students.find((s) => s.rank === "1");
                            if (w) navigateToSearch(w.name);
                          }}
                          className="hover:text-yellow-600 hover:underline transition-colors text-left"
                        >
                          {currentYearData.students.find((s) => s.rank === "1")?.name || "N/A"}
                        </button>
                      </p>
                      <p className="text-gray-600 flex items-center font-medium">
                        <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                        <button
                          onClick={() => {
                            const w = currentYearData.students.find((s) => s.rank === "1");
                            if (w) handleStateChange(w.state);
                          }}
                          className="hover:text-blue-600 hover:underline transition-colors"
                        >
                          {currentYearData.students.find((s) => s.rank === "1")?.state || "N/A"}
                        </button>
                      </p>
                      <Trophy className="absolute -bottom-2 -right-2 h-16 w-16 text-yellow-500/10 group-hover:text-yellow-500/20 transition-all" />
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 border border-blue-100 relative overflow-hidden group hover:shadow-md transition-all">
                      <p className="text-xs font-black text-blue-600 uppercase tracking-[0.2em] mb-2">
                        National Team Champion
                      </p>
                      <p className="text-2xl font-black text-gray-900 mb-1">
                        <button
                          onClick={() => {
                            const t = currentYearData.teams.find((t) => t.rank === "1");
                            if (t) {
                              const abbr = reverseStateMap[t.name];
                              if (abbr) handleStateChange(abbr);
                            }
                          }}
                          className="hover:text-blue-600 hover:underline transition-colors text-left"
                        >
                          {currentYearData.teams.find((t) => t.rank === "1")?.name || "N/A"}
                        </button>
                      </p>
                      <p className="text-gray-600 flex items-center font-medium">
                        <Award className="h-4 w-4 mr-1 text-gray-400" />
                        Rank #1 Team Finish
                      </p>
                      <Award className="absolute -bottom-2 -right-2 h-16 w-16 text-blue-500/10 group-hover:text-blue-500/20 transition-all" />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-gradient-to-br from-yellow-50 to-white rounded-2xl p-6 border border-yellow-200 relative overflow-hidden group hover:shadow-md transition-all">
                        <p className="text-xs font-black text-yellow-600 uppercase tracking-[0.2em] mb-2">
                          National Champion (Countdown)
                        </p>
                        <p className="text-2xl font-black text-gray-900 mb-1">
                          {!currentYearData.noCountdown ? (
                            <button
                              onClick={() => {
                                const w = currentYearData.students.find(
                                  (s) => s.rank === "1",
                                );
                                if (w) navigateToSearch(w.name);
                              }}
                              className="hover:text-yellow-600 hover:underline transition-colors text-left"
                            >
                              {currentYearData.students.find((s) => s.rank === "1")
                                ?.name || "N/A"}
                            </button>
                          ) : (
                            "N/A"
                          )}
                        </p>
                        <p className="text-gray-600 flex items-center font-medium">
                          <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                          <button
                            onClick={() => {
                              const winner = !currentYearData.noCountdown
                                ? currentYearData.students.find(
                                    (s) => s.rank === "1",
                                  )
                                : null;
                              if (winner) handleStateChange(winner.state);
                            }}
                            className="hover:text-blue-600 hover:underline transition-colors"
                          >
                            {!currentYearData.noCountdown
                              ? currentYearData.students.find((s) => s.rank === "1")
                                  ?.state || "N/A"
                              : "N/A"}
                          </button>
                        </p>
                        <Trophy className="absolute -bottom-2 -right-2 h-16 w-16 text-yellow-500/10 group-hover:text-yellow-500/20 transition-all" />
                      </div>
                      <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200 relative overflow-hidden group hover:shadow-md transition-all">
                        <p className="text-xs font-black text-gray-500 uppercase tracking-[0.2em] mb-2">
                          National Runner-Up
                        </p>
                        <p className="text-2xl font-black text-gray-900 mb-1">
                          {!currentYearData.noCountdown ? (
                            <button
                              onClick={() => {
                                const r = currentYearData.students.find(
                                  (s) => s.rank === "2",
                                );
                                if (r) navigateToSearch(r.name);
                              }}
                              className="hover:text-gray-500 hover:underline transition-colors text-left"
                            >
                              {currentYearData.students.find((s) => s.rank === "2")
                                ?.name || "N/A"}
                            </button>
                          ) : (
                            "N/A"
                          )}
                        </p>
                        <p className="text-gray-600 flex items-center font-medium">
                          <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                          <button
                            onClick={() => {
                              const runnerUp = !currentYearData.noCountdown
                                ? currentYearData.students.find(
                                    (s) => s.rank === "2",
                                  )
                                : null;
                              if (runnerUp) handleStateChange(runnerUp.state);
                            }}
                            className="hover:text-blue-600 hover:underline transition-colors"
                          >
                            {!currentYearData.noCountdown
                              ? currentYearData.students.find((s) => s.rank === "2")
                                  ?.state || "N/A"
                              : "N/A"}
                          </button>
                        </p>
                        <Trophy className="absolute -bottom-2 -right-2 h-16 w-16 text-gray-400/10 group-hover:text-gray-400/20 transition-all" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 border border-blue-100 relative overflow-hidden group hover:shadow-md transition-all">
                        <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-2">
                          Written Champion
                        </p>
                        <p className="text-2xl font-extrabold text-gray-900 mb-1">
                          {currentYearData.writtenChampion?.name ? (
                            <button
                              onClick={() =>
                                navigateToSearch(
                                  currentYearData.writtenChampion.name,
                                )
                              }
                              className="hover:text-blue-600 hover:underline transition-colors text-left"
                            >
                              {currentYearData.writtenChampion.name}
                            </button>
                          ) : (
                            "N/A"
                          )}
                        </p>
                        <p className="text-gray-600 flex items-center font-medium">
                          <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                          <button
                            onClick={() =>
                              handleStateChange(
                                currentYearData.writtenChampion?.state,
                              )
                            }
                            className="hover:text-blue-600 hover:underline transition-colors"
                          >
                            {currentYearData.writtenChampion?.state || "N/A"}
                          </button>
                        </p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-white rounded-2xl p-6 border border-purple-100 relative overflow-hidden group hover:shadow-md transition-all">
                        <p className="text-xs font-bold text-purple-600 uppercase tracking-widest mb-2">
                          Written Runner-Up
                        </p>
                        <p className="text-2xl font-extrabold text-gray-900 mb-1">
                          {currentYearData.writtenRunnerUp?.name ? (
                            <button
                              onClick={() =>
                                navigateToSearch(
                                  currentYearData.writtenRunnerUp.name,
                                )
                              }
                              className="hover:text-purple-600 hover:underline transition-colors text-left"
                            >
                              {currentYearData.writtenRunnerUp.name}
                            </button>
                          ) : (
                            "N/A"
                          )}
                        </p>
                        <p className="text-gray-600 flex items-center font-medium">
                          <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                          <button
                            onClick={() =>
                              handleStateChange(
                                currentYearData.writtenRunnerUp?.state,
                              )
                            }
                            className="hover:text-blue-600 hover:underline transition-colors"
                          >
                            {currentYearData.writtenRunnerUp?.state || "N/A"}
                          </button>
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {currentYearData.isPartial ? (
            <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-6 flex items-start gap-4 shadow-sm animate-in fade-in duration-300">
              <div className="p-3 bg-blue-100/60 rounded-xl text-blue-600 shadow-sm flex-shrink-0 animate-pulse">
                <Info className="h-6 w-6" />
              </div>
              <div>
                <h4 className="font-bold text-blue-900 text-lg">Historical Archive Record</h4>
                <p className="text-blue-700 text-sm mt-1 font-medium">
                  For competitions prior to 2008, only official individual and team national champion records are currently preserved in our database. Complete top 56 individual student standings and stats are not available.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-green-50 rounded-lg text-green-600">
                    <Users className="h-6 w-6" />
                  </div>
                  <span className="text-xs font-bold text-gray-400 uppercase">
                    Participants
                  </span>
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {yearlyStats.totalStudents}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  Ranked Individuals
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <span className="text-xs font-bold text-gray-400 uppercase">
                    Top State
                  </span>
                </div>
                <div className="text-3xl font-bold text-gray-900 line-clamp-1">
                  {yearlyStats.topState}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  Most Students in Top 56
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-pink-50 rounded-lg text-pink-600">
                    <BarChart2 className="h-6 w-6" />
                  </div>
                  <span className="text-xs font-bold text-gray-400 uppercase">
                    Demographics
                  </span>
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {yearlyStats.genderRatio}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  Gender Distribution (Est.)
                </div>
              </div>
            </div>
          )}

          {currentYearData.isPartial ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center flex flex-col items-center animate-in fade-in duration-300">
              <div className="p-4 bg-gray-50 rounded-full text-gray-400 mb-4">
                <Users className="h-10 w-10 text-blue-500/80" />
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">Detailed Standings Unavailable</h4>
              <p className="text-gray-500 max-w-md text-sm font-medium">
                Complete top 56 individual student listings and full team rankings are only available for 2008 and later. You can view the champions above or search for individual records in the Search tab.
              </p>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <h3 className="text-xl font-bold text-gray-900">
                    Top {currentYearData?.students.length} Individual Students
                  </h3>
                  <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                    <select
                      value={selectedGrade}
                      onChange={(e) => setSelectedGrade(e.target.value)}
                      className="px-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium text-gray-600"
                    >
                      <option value="All">All Grades</option>
                      <option value="6">Grade 6</option>
                      <option value="7">Grade 7</option>
                      <option value="8">Grade 8</option>
                    </select>
                    <select
                      value={selectedGender}
                      onChange={(e) => setSelectedGender(e.target.value)}
                      className="px-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium text-gray-600"
                    >
                      <option value="All">All Genders</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-gray-50/50">
                      <tr>
                        <th
                          className="px-6 py-4 text-xs font-bold text-gray-500 uppercase cursor-pointer group"
                          onClick={() => handleSort("rank")}
                        >
                          <div className="flex items-center">
                            Rank <SortIcon column="rank" />
                          </div>
                        </th>
                        <th
                          className="px-6 py-4 text-xs font-bold text-gray-500 uppercase cursor-pointer group"
                          onClick={() => handleSort("name")}
                        >
                          <div className="flex items-center">
                            Student <SortIcon column="name" />
                          </div>
                        </th>
                        <th
                          className="px-6 py-4 text-xs font-bold text-gray-500 uppercase cursor-pointer group"
                          onClick={() => handleSort("state")}
                        >
                          <div className="flex items-center">
                            State <SortIcon column="state" />
                          </div>
                        </th>
                        <th
                          className="px-6 py-4 text-xs font-bold text-gray-500 uppercase cursor-pointer group"
                          onClick={() => handleSort("grade")}
                        >
                          <div className="flex items-center">
                            Grade <SortIcon column="grade" />
                          </div>
                        </th>
                        <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">
                          Gender
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filteredStudents.map((student, idx) => (
                        <tr
                          key={`${student.name}-${idx}`}
                          className="hover:bg-blue-50/30 transition-colors"
                        >
                          <td className="px-6 py-4">
                            <span
                              className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-sm font-bold ${
                                student.rank === "1"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : student.rank === "2"
                                    ? "bg-gray-200 text-gray-700"
                                    : ["S", "Q", "C", "P"].includes(student.rank)
                                      ? "bg-blue-100 text-blue-700"
                                      : "bg-gray-50 text-gray-500"
                              }`}
                            >
                              {student.rank}
                            </span>
                          </td>
                          <td className="px-6 py-4 font-medium text-gray-900">
                            <button
                              onClick={() => navigateToSearch(student.name)}
                              className="hover:text-blue-600 hover:underline transition-colors text-left"
                            >
                              {student.name}
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleStateChange(student.state)}
                              className="text-gray-600 hover:text-blue-600 hover:underline transition-colors font-medium"
                            >
                              {student.state}
                            </button>
                          </td>
                          <td className="px-6 py-4 text-gray-600">
                            {student.grade}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`text-xs px-2 py-1 rounded-full font-bold ${
                                student.gender === "Female"
                                  ? "bg-pink-100 text-pink-600"
                                  : student.gender === "Male"
                                    ? "bg-blue-100 text-blue-600"
                                    : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {student.gender}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <Filter className="h-5 w-5 text-blue-600 mr-2" />
                    Top 23 National Teams ({selectedYear})
                  </h3>
                </div>
                <div className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-12">
                    {currentYearData.teams.map((team) => (
                      <div
                        key={team.name}
                        className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0 group"
                      >
                        <div className="flex items-center">
                          <span className="text-xs font-extrabold text-gray-300 w-8 group-hover:text-blue-400 transition-colors">
                            {team.rank}.
                          </span>
                          <button
                            onClick={() => {
                              const abbr = reverseStateMap[team.name];
                              if (abbr) handleStateChange(abbr);
                            }}
                            className="text-gray-700 font-semibold hover:text-blue-600 hover:underline transition-colors"
                          >
                            {team.name}
                          </button>
                        </div>
                        {team.rank <= 3 && (
                          <Trophy
                            className={`h-4 w-4 ${
                              team.rank === 1
                                ? "text-yellow-500"
                                : team.rank === 2
                                  ? "text-gray-400"
                                  : "text-amber-600"
                            }`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === "state" && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <MapPin className="h-5 w-5 text-blue-600 mr-2" />
                Select State
              </h3>
              <div className="grid grid-cols-5 gap-2">
                {allStates.map((state) => (
                  <button
                    key={state}
                    onClick={() => handleStateChange(state)}
                    className={`py-2 px-1 rounded-lg text-xs font-bold transition-all border ${
                      selectedState === state
                        ? "bg-blue-600 text-white border-blue-600 shadow-md"
                        : "bg-white text-gray-600 border-gray-100 hover:border-blue-200 hover:text-blue-600"
                    }`}
                  >
                    {state}
                  </button>
                ))}
              </div>
            </div>

            <div className="lg:col-span-2 bg-white rounded-2xl p-8 shadow-sm border border-gray-100 flex flex-col h-full">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
                <h3 className="text-2xl font-black text-gray-900 flex items-center">
                  <MapPin className="h-6 w-6 text-blue-600 mr-2" />
                  {stateNameMap[selectedState] || selectedState}
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-grow">
                <div className="flex flex-col justify-center bg-blue-50/60 p-6 rounded-2xl border border-blue-100">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 rounded-xl text-blue-600 shadow-sm">
                      <TrendingUp className="h-8 w-8" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Total Top 56 Apperances
                      </p>
                      <p className="text-3xl font-black text-gray-900">
                        {stateData.totalAppearances}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col justify-center bg-indigo-50/60 p-6 rounded-2xl border border-indigo-100">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-indigo-100 rounded-xl text-indigo-600 shadow-sm">
                      <Users className="h-8 w-8" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Total Unique Students
                      </p>
                      <p className="text-3xl font-black text-gray-900">
                        {stateData.uniqueStudentsCount}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col justify-center bg-yellow-50/60 p-6 rounded-2xl border border-yellow-100">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-yellow-100 rounded-xl text-yellow-600 shadow-sm">
                      <Trophy className="h-8 w-8" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Best Individual Rank
                      </p>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <p className="text-3xl font-black text-gray-900">
                          {stateData.bestRank === 999 ? "N/A" : stateData.bestRank}
                        </p>
                        {stateData.bestRank !== 999 && stateData.bestRankYears.length > 0 && (
                          <span className="text-xs text-gray-500 font-semibold bg-yellow-100/50 px-2 py-0.5 rounded-md">
                            ({stateData.bestRankYears.join(", ")})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col justify-center bg-purple-50/60 p-6 rounded-2xl border border-purple-100">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-purple-100 rounded-xl text-purple-600 shadow-sm">
                      <Award className="h-8 w-8" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Best Team Rank
                      </p>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <p className="text-3xl font-black text-gray-900">
                          {stateData.bestTeamRank === 999
                            ? "N/A"
                            : stateData.bestTeamRank}
                        </p>
                        {stateData.bestTeamRank !== 999 && stateData.bestTeamRankYears.length > 0 && (
                          <span className="text-xs text-gray-500 font-semibold bg-purple-100/50 px-2 py-0.5 rounded-md">
                            ({stateData.bestTeamRankYears.join(", ")})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* State Student History */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">
                  Historical Top 56 Students
                </h3>
                <div className="flex items-center gap-2">
                  <select
                    value={stateYearFilter}
                    onChange={(e) => setStateYearFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium text-gray-600"
                  >
                    <option value="All">All Years</option>
                    {availableStateYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <select
                    value={stateGenderFilter}
                    onChange={(e) => setStateGenderFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium text-gray-600"
                  >
                    <option value="All">All Genders</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-gray-50/50">
                    <tr>
                      <th
                        className="px-6 py-4 text-xs font-bold text-gray-500 uppercase cursor-pointer hover:text-blue-600 transition-colors"
                        onClick={() => handleStateStudentsSort("year")}
                      >
                        <span className="flex items-center gap-1">
                          Year{" "}
                          {stateStudentsSort.key === "year" ? (
                            stateStudentsSort.dir === "asc" ? (
                              <ChevronUp className="h-3 w-3" />
                            ) : (
                              <ChevronDown className="h-3 w-3" />
                            )
                          ) : (
                            <ChevronDown className="h-3 w-3 opacity-20" />
                          )}
                        </span>
                      </th>
                      <th
                        className="px-6 py-4 text-xs font-bold text-gray-500 uppercase cursor-pointer hover:text-blue-600 transition-colors"
                        onClick={() => handleStateStudentsSort("rank")}
                      >
                        <span className="flex items-center gap-1">
                          Rank{" "}
                          {stateStudentsSort.key === "rank" ? (
                            stateStudentsSort.dir === "asc" ? (
                              <ChevronUp className="h-3 w-3" />
                            ) : (
                              <ChevronDown className="h-3 w-3" />
                            )
                          ) : (
                            <ChevronDown className="h-3 w-3 opacity-20" />
                          )}
                        </span>
                      </th>
                      <th
                        className="px-6 py-4 text-xs font-bold text-gray-500 uppercase cursor-pointer hover:text-blue-600 transition-colors"
                        onClick={() => handleStateStudentsSort("name")}
                      >
                        <span className="flex items-center gap-1">
                          Student{" "}
                          {stateStudentsSort.key === "name" ? (
                            stateStudentsSort.dir === "asc" ? (
                              <ChevronUp className="h-3 w-3" />
                            ) : (
                              <ChevronDown className="h-3 w-3" />
                            )
                          ) : (
                            <ChevronDown className="h-3 w-3 opacity-20" />
                          )}
                        </span>
                      </th>
                      <th
                        className="px-6 py-4 text-xs font-bold text-gray-500 uppercase cursor-pointer hover:text-blue-600 transition-colors"
                        onClick={() => handleStateStudentsSort("gender")}
                      >
                        <span className="flex items-center gap-1">
                          Gender{" "}
                          {stateStudentsSort.key === "gender" ? (
                            stateStudentsSort.dir === "asc" ? (
                              <ChevronUp className="h-3 w-3" />
                            ) : (
                              <ChevronDown className="h-3 w-3" />
                            )
                          ) : (
                            <ChevronDown className="h-3 w-3 opacity-20" />
                          )}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredStateStudents.map((student, idx) => (
                      <tr
                        key={`${student.year}-${student.name}-${idx}`}
                        className="hover:bg-blue-50/30 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleYearChange(student.year)}
                            className="font-bold text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                          >
                            {student.year}
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center justify-center h-7 w-7 rounded-full text-xs font-bold ${
                              ["1", "2", "S", "Q"].includes(student.rank)
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {student.rank}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-900">
                          <button
                            onClick={() => navigateToSearch(student.name)}
                            className="hover:text-blue-600 hover:underline transition-colors text-left"
                          >
                            {student.name}
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`text-xs px-2 py-1 rounded-full font-bold ${
                              student.gender === "Female"
                                ? "bg-pink-100 text-pink-600"
                                : student.gender === "Male"
                                  ? "bg-blue-100 text-blue-600"
                                  : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {student.gender || "N/A"}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {filteredStateStudents.length === 0 && (
                      <tr>
                        <td
                          colSpan="3"
                          className="px-6 py-12 text-center text-gray-500 italic"
                        >
                          No top 56 students found for this state in the
                          records.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h3 className="text-xl font-bold text-gray-900">
                  Team Performance History
                </h3>
              </div>
              <div className="p-6">
                {stateData.teams.length > 0 ? (
                  <div className="space-y-4">
                    {stateData.teams.map((team) => (
                      <div
                        key={team.year}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 group hover:border-blue-200 transition-all"
                      >
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => handleYearChange(team.year)}
                            className="text-lg font-black text-gray-400 group-hover:text-blue-500 hover:underline transition-all"
                          >
                            {team.year}
                          </button>
                          <div className="h-8 w-px bg-gray-200"></div>
                          <div className="font-bold text-gray-700 uppercase tracking-widest text-xs">
                            National Rank
                          </div>
                        </div>
                        <div
                          className={`text-2xl font-black ${
                            team.rank <= 3 ? "text-yellow-600" : "text-blue-600"
                          }`}
                        >
                          #{team.rank}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center text-gray-500 italic">
                    No top 23 team finishes recorded for this state.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "statistics" && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* Shared year-range filter */}
          <div className="bg-blue-50 border border-blue-100 rounded-2xl px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <span className="text-sm font-bold text-blue-700 whitespace-nowrap">
              Year Range:
            </span>
            <div className="flex items-center gap-3 flex-wrap">
              <select
                value={statsFromYear ?? ""}
                onChange={(e) =>
                  setStatsFromYear(
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="px-3 py-2 rounded-xl border border-blue-200 bg-white text-gray-700 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">Earliest</option>
                {allYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <span className="text-gray-400 font-bold">—</span>
              <select
                value={statsToYear ?? ""}
                onChange={(e) =>
                  setStatsToYear(
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="px-3 py-2 rounded-xl border border-blue-200 bg-white text-gray-700 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">Latest</option>
                {[...allYears].reverse().map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              {(statsFromYear || statsToYear) && (
                <button
                  onClick={() => {
                    setStatsFromYear(null);
                    setStatsToYear(null);
                  }}
                  className="px-3 py-2 rounded-xl text-xs font-bold text-blue-600 bg-white border border-blue-200 hover:bg-blue-100 transition-colors"
                >
                  Reset
                </button>
              )}
            </div>
            <span className="text-xs text-blue-500 ml-auto">
              Showing: {statsFromYear ?? allYears[0]} –{" "}
              {statsToYear ?? allYears[allYears.length - 1]}
            </span>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Award className="h-6 w-6 text-blue-600" />
                  State Powerhouse Index
                </h3>
                <p className="text-gray-500 mt-1">
                  Top 15 states with most individual top 56 qualifications
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-gray-400 uppercase tracking-wider">
                  Total Records
                </p>
                <p className="text-2xl font-black text-blue-600">
                  {aggregateStats.totalStudentsCount}
                </p>
              </div>
            </div>

            <div className="h-[500px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={aggregateStats.top15States}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                >
                  <defs>
                    <linearGradient
                      id="barGradient"
                      x1="0"
                      y1="0"
                      x2="1"
                      y2="0"
                    >
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.8} />
                      <stop offset="100%" stopColor="#60a5fa" stopOpacity={1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={true}
                    vertical={false}
                    stroke="#f1f5f9"
                  />
                  <XAxis type="number" hide />
                  <YAxis
                    dataKey="fullName"
                    type="category"
                    width={100}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#475569", fontSize: 13, fontWeight: 600 }}
                  />
                  <Tooltip
                    cursor={{ fill: "#f8fafc" }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-white p-4 shadow-xl rounded-xl border border-gray-100">
                            <p className="font-bold text-gray-900">
                              {payload[0].payload.fullName}
                            </p>
                            <p className="text-blue-600 font-bold">
                              {payload[0].value} Top 56 Appearances
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar
                    dataKey="count"
                    radius={[0, 8, 8, 0]}
                    fill="url(#barGradient)"
                    barSize={24}
                  >
                    {aggregateStats.top15States.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fillOpacity={1 - index * 0.04}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <TrendingUp className="h-6 w-6 text-emerald-600" />
                  Weighted Performance Index
                </h3>
                <p className="text-gray-500 mt-1">
                  States ranked by quality of finishes (Champion=56pts,
                  S=53.5pts, Q=50.5pts, etc.)
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-gray-400 uppercase tracking-wider">
                  Scoring Method
                </p>
                <p className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">
                  Rank-Based Weighting
                </p>
              </div>
            </div>

            <div className="h-[500px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={aggregateStats.weightedStates}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                >
                  <defs>
                    <linearGradient
                      id="weightedGradient"
                      x1="0"
                      y1="0"
                      x2="1"
                      y2="0"
                    >
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.8} />
                      <stop offset="100%" stopColor="#34d399" stopOpacity={1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={true}
                    vertical={false}
                    stroke="#f1f5f9"
                  />
                  <XAxis type="number" hide />
                  <YAxis
                    dataKey="fullName"
                    type="category"
                    width={100}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#475569", fontSize: 13, fontWeight: 600 }}
                  />
                  <Tooltip
                    cursor={{ fill: "#f0fdf4" }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-white p-4 shadow-xl rounded-xl border border-gray-100">
                            <p className="font-bold text-gray-900">
                              {payload[0].payload.fullName}
                            </p>
                            <p className="text-emerald-600 font-bold">
                              {payload[0].value} Quality Points
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Based on historical Top 56 ranks
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar
                    dataKey="weight"
                    radius={[0, 8, 8, 0]}
                    fill="url(#weightedGradient)"
                    barSize={24}
                  >
                    {aggregateStats.weightedStates.map((entry, index) => (
                      <Cell
                        key={`cell-weighted-${index}`}
                        fillOpacity={1 - index * 0.04}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
              <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                <Trophy className="h-6 w-6 text-yellow-500 mr-2" />
                Historical Team Rankings
              </h3>
              <button
                onClick={() => setChampionsOnly((prev) => !prev)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all self-start sm:self-auto ${
                  championsOnly
                    ? "bg-yellow-500 text-white shadow-md hover:bg-yellow-600"
                    : "bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100"
                }`}
              >
                <Trophy className="h-4 w-4" />
                Champions Only
              </button>
            </div>
            <div className="overflow-x-auto pb-4">
              <table className="w-full text-left border-collapse min-w-max">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase sticky left-0 bg-gray-50 z-10 shadow-[1px_0_0_0_#f3f4f6]">
                      State
                    </th>
                    {championsOnly && (
                      <th className="px-4 py-4 text-xs font-bold text-yellow-600 uppercase text-center whitespace-nowrap bg-yellow-50">
                        🏆 Championships
                      </th>
                    )}
                    {teamRankingsMatrix.years.map((year) => (
                      <th
                        key={year}
                        className="px-4 py-4 text-xs font-bold text-gray-500 uppercase text-center"
                      >
                        <button
                          onClick={() => {
                            setSelectedYear(year);
                            setActiveTab("yearly");
                            navigate(`/mathcounts/year/${year}`);
                          }}
                          className="hover:text-blue-600 hover:underline transition-colors"
                        >
                          {year}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {teamRankingsMatrix.states
                    .filter(
                      (state) =>
                        !championsOnly ||
                        Object.values(teamRankingsMatrix.stateMap[state]).some(
                          (r) => r === 1,
                        ),
                    )
                    .sort((a, b) => {
                      if (!championsOnly) return 0;
                      const countA = Object.values(
                        teamRankingsMatrix.stateMap[a],
                      ).filter((r) => r === 1).length;
                      const countB = Object.values(
                        teamRankingsMatrix.stateMap[b],
                      ).filter((r) => r === 1).length;
                      return countB - countA;
                    })
                    .map((state) => {
                      const champCount = Object.values(
                        teamRankingsMatrix.stateMap[state],
                      ).filter((r) => r === 1).length;
                      return (
                        <tr
                          key={state}
                          className="hover:bg-blue-50/30 transition-colors group"
                        >
                          <td className="px-6 py-4 font-bold text-gray-700 sticky left-0 bg-white group-hover:bg-blue-50/50 z-10 shadow-[1px_0_0_0_#f3f4f6]">
                            {state}
                          </td>
                          {championsOnly && (
                            <td className="px-4 py-4 text-center bg-yellow-50/50 group-hover:bg-yellow-50">
                              <span className="inline-flex items-center justify-center gap-1 px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 font-black text-sm">
                                <Trophy className="h-3 w-3" />
                                {champCount}
                              </span>
                            </td>
                          )}
                          {teamRankingsMatrix.years.map((year) => {
                            const rank =
                              teamRankingsMatrix.stateMap[state][year];
                            return (
                              <td key={year} className="px-4 py-4 text-center">
                                {rank ? (
                                  <span
                                    className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-sm font-bold ${
                                      rank === 1
                                        ? "bg-yellow-500 text-white shadow-sm"
                                        : rank <= 3
                                          ? "bg-yellow-100 text-yellow-700"
                                          : rank <= 10
                                            ? "bg-blue-100 text-blue-700"
                                            : "bg-gray-100 text-gray-600"
                                    }`}
                                  >
                                    {rank === 1 ? (
                                      <Trophy className="h-4 w-4" />
                                    ) : (
                                      rank
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-gray-300">-</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-6 gap-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Award className="h-6 w-6 text-purple-500 mr-2" />
                  Individual Champions by State
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  States with at least one top-2 individual finish (any
                  category)
                </p>
              </div>
              <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl self-start shrink-0">
                <button
                  onClick={() => setChampionsViewMode("count")}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                    championsViewMode === "count"
                      ? "bg-white text-gray-800 shadow-sm"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  Count
                </button>
                <button
                  onClick={() => setChampionsViewMode("detail")}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                    championsViewMode === "detail"
                      ? "bg-white text-gray-800 shadow-sm"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  Detail
                </button>
              </div>
            </div>
            <div className="overflow-x-auto pb-4">
              <table className="w-full text-left border-collapse min-w-max">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th
                      className="px-6 py-4 text-xs font-bold text-gray-500 uppercase sticky left-0 bg-gray-50 z-10 shadow-[1px_0_0_0_#f3f4f6] cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() => handleChampSort("state")}
                    >
                      <span className="flex items-center gap-1">
                        State{" "}
                        {champTableSort.key === "state" ? (
                          champTableSort.dir === "asc" ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )
                        ) : (
                          <ChevronDown className="h-3 w-3 opacity-20" />
                        )}
                      </span>
                    </th>
                    {[
                      [
                        "cdChamp",
                        "text-yellow-600",
                        "bg-yellow-50/50",
                        "🥇 Countdown Champion",
                      ],
                      [
                        "cdRunnerUp",
                        "text-gray-500",
                        "bg-gray-50/50",
                        "🥈 Countdown Runner-Up",
                      ],
                      [
                        "wrChamp",
                        "text-blue-600",
                        "bg-blue-50/50",
                        "✍️ Written Champion",
                      ],
                      [
                        "wrRunnerUp",
                        "text-purple-600",
                        "bg-purple-50/50",
                        "✍️ Written Runner-Up",
                      ],
                      ["total", "text-gray-600", "", "Total"],
                    ].map(([key, color, bg, label]) => (
                      <th
                        key={key}
                        onClick={() => handleChampSort(key)}
                        className={`px-4 py-4 text-xs font-bold ${color} uppercase text-center whitespace-nowrap ${bg} cursor-pointer hover:opacity-80 transition-opacity`}
                      >
                        <span className="inline-flex items-center justify-center gap-1">
                          {label}{" "}
                          {champTableSort.key === key ? (
                            champTableSort.dir === "asc" ? (
                              <ChevronUp className="h-3 w-3" />
                            ) : (
                              <ChevronDown className="h-3 w-3" />
                            )
                          ) : (
                            <ChevronDown className="h-3 w-3 opacity-20" />
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {[...individualChampionsMatrix]
                    .sort((a, b) => {
                      const { key, dir } = champTableSort;
                      const aVal =
                        key === "state" ? a.state : (a[key].length ?? a[key]);
                      const bVal =
                        key === "state" ? b.state : (b[key].length ?? b[key]);
                      if (aVal < bVal) return dir === "asc" ? -1 : 1;
                      if (aVal > bVal) return dir === "asc" ? 1 : -1;
                      return 0;
                    })
                    .map((row) => (
                      <tr
                        key={row.state}
                        className="hover:bg-blue-50/30 transition-colors group align-top"
                      >
                        <td className="px-6 py-4 font-bold text-gray-700 sticky left-0 bg-white group-hover:bg-blue-50/50 z-10 shadow-[1px_0_0_0_#f3f4f6]">
                          <button
                            onClick={() => handleStateChange(row.state)}
                            className="hover:text-blue-600 hover:underline transition-colors"
                          >
                            {row.state}
                          </button>
                        </td>
                        {[
                          [
                            "cdChamp",
                            "bg-yellow-50/20",
                            "text-yellow-700 bg-yellow-100",
                            "bg-yellow-500",
                          ],
                          [
                            "cdRunnerUp",
                            "",
                            "text-gray-600 bg-gray-100",
                            "bg-gray-400",
                          ],
                          [
                            "wrChamp",
                            "bg-blue-50/20",
                            "text-blue-700 bg-blue-100",
                            "bg-blue-500",
                          ],
                          [
                            "wrRunnerUp",
                            "bg-purple-50/20",
                            "text-purple-700 bg-purple-100",
                            "bg-purple-400",
                          ],
                        ].map(([cat, cellBg, badge, countBg]) => (
                          <td
                            key={cat}
                            className={`px-4 py-4 ${championsViewMode === "detail" ? "text-left" : "text-center"} ${cellBg}`}
                          >
                            {row[cat].length > 0 ? (
                              championsViewMode === "count" ? (
                                <span
                                  className={`inline-flex items-center justify-center h-8 w-8 rounded-full ${countBg} text-white font-black text-sm shadow-sm`}
                                >
                                  {row[cat].length}
                                </span>
                              ) : (
                                <div className="space-y-1">
                                  {row[cat].map((entry) => (
                                    <div
                                      key={entry.year}
                                      className="flex items-center gap-2"
                                    >
                                      <span
                                        className={`text-xs font-black px-2 py-0.5 rounded-full ${badge}`}
                                      >
                                        {entry.year}
                                      </span>
                                      <button
                                        onClick={() =>
                                          navigateToSearch(entry.name)
                                        }
                                        className="text-sm text-gray-700 font-medium hover:text-blue-600 hover:underline transition-colors text-left"
                                      >
                                        {entry.name}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                        ))}
                        <td className="px-4 py-4 text-center">
                          <span className="font-black text-gray-800 text-lg">
                            {row.total}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {activeTab === "halloffame" && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* Top Hero Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 rounded-2xl p-6 border border-yellow-200/50 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-black uppercase text-yellow-700 bg-yellow-100 px-3 py-1 rounded-full">
                  🥇 First-Ever Champion
                </span>
                <Trophy className="h-6 w-6 text-yellow-500" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 group-hover:text-yellow-600 transition-colors">
                Michael Edwards
              </h4>
              <p className="text-gray-500 text-sm mt-1">
                Representing <span className="font-semibold text-gray-700">Texas</span> • Year 1984
              </p>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => navigateToSearch("Michael Edwards")}
                  className="text-xs font-bold text-yellow-700 hover:text-yellow-800 underline transition-colors"
                >
                  View Profile
                </button>
                <span className="text-gray-300">•</span>
                <button
                  onClick={() => handleYearChange(1984)}
                  className="text-xs font-bold text-yellow-700 hover:text-yellow-800 underline transition-colors"
                >
                  View 1984 Results
                </button>
              </div>
            </div>

            {mostDecorated && (
              <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 rounded-2xl p-6 border border-blue-200/50 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-black uppercase text-blue-700 bg-blue-100 px-3 py-1 rounded-full">
                    👑 Most Decorated Champion
                  </span>
                  <Award className="h-6 w-6 text-blue-500" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                  {mostDecorated.name}
                </h4>
                <p className="text-gray-500 text-sm mt-1">
                  Representing <span className="font-semibold text-gray-700">{stateNameMap[mostDecorated.state] || mostDecorated.state}</span> • {mostDecorated.wins}-Time Champion
                </p>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => navigateToSearch(mostDecorated.name)}
                    className="text-xs font-bold text-blue-700 hover:text-blue-800 underline transition-colors"
                  >
                    View Profile
                  </button>
                  <span className="text-gray-300">•</span>
                  <button
                    onClick={() => handleYearChange(Math.max(...mostDecorated.years))}
                    className="text-xs font-bold text-blue-700 hover:text-blue-800 underline transition-colors"
                  >
                    View {Math.max(...mostDecorated.years)} Results
                  </button>
                </div>
              </div>
            )}

            <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 rounded-2xl p-6 border border-purple-200/50 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-black uppercase text-purple-700 bg-purple-100 px-3 py-1 rounded-full">
                  🌟 National Legends
                </span>
                <Users className="h-6 w-6 text-purple-500" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 group-hover:text-purple-600 transition-colors">
                {nationalChampions.length} Unique Champions
              </h4>
              <p className="text-gray-500 text-sm mt-1">
                Across {allYears.length} years of elite competition
              </p>
              <div className="mt-4">
                <span className="text-xs font-semibold text-purple-700 bg-purple-100/50 px-2 py-0.5 rounded-lg">
                  1984 – 2026 History Preserved
                </span>
              </div>
            </div>
          </div>

          {/* Section 1: National Individual Champions */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Trophy className="h-6 w-6 text-yellow-500 mr-2 animate-pulse" />
                  National Individual Champions (Countdown Winners)
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  Individual champions who won the national countdown round
                </p>
              </div>
              <div>
                <select
                  value={hallOfFameChampGenderFilter}
                  onChange={(e) => setHallOfFameChampGenderFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium text-gray-600"
                >
                  <option value="All">All Genders</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">
                      #
                    </th>
                    <th
                      className="px-6 py-4 text-xs font-bold text-gray-500 uppercase cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() => handleHallOfFameChampSort("name")}
                    >
                      <span className="flex items-center gap-1">
                        Student{" "}
                        {hallOfFameChampSort.key === "name" ? (
                          hallOfFameChampSort.dir === "asc" ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )
                        ) : (
                          <ChevronDown className="h-3 w-3 opacity-20" />
                        )}
                      </span>
                    </th>
                    <th
                      className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-center cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() => handleHallOfFameChampSort("state")}
                    >
                      <span className="flex items-center justify-center gap-1">
                        State{" "}
                        {hallOfFameChampSort.key === "state" ? (
                          hallOfFameChampSort.dir === "asc" ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )
                        ) : (
                          <ChevronDown className="h-3 w-3 opacity-20" />
                        )}
                      </span>
                    </th>
                    <th
                      className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-center cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() => handleHallOfFameChampSort("gender")}
                    >
                      <span className="flex items-center justify-center gap-1">
                        Gender{" "}
                        {hallOfFameChampSort.key === "gender" ? (
                          hallOfFameChampSort.dir === "asc" ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )
                        ) : (
                          <ChevronDown className="h-3 w-3 opacity-20" />
                        )}
                      </span>
                    </th>
                    <th
                      className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-center cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() => handleHallOfFameChampSort("wins")}
                    >
                      <span className="flex items-center justify-center gap-1">
                        Wins{" "}
                        {hallOfFameChampSort.key === "wins" ? (
                          hallOfFameChampSort.dir === "asc" ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )
                        ) : (
                          <ChevronDown className="h-3 w-3 opacity-20" />
                        )}
                      </span>
                    </th>
                    <th
                      className="px-6 py-4 text-xs font-bold text-gray-500 uppercase cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() => handleHallOfFameChampSort("years")}
                    >
                      <span className="flex items-center gap-1">
                        Years of Victory{" "}
                        {hallOfFameChampSort.key === "years" ? (
                          hallOfFameChampSort.dir === "asc" ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )
                        ) : (
                          <ChevronDown className="h-3 w-3 opacity-20" />
                        )}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {nationalChampions.map((student, idx) => (
                    <tr
                      key={`${student.name}-${student.state}-${idx}`}
                      className="hover:bg-blue-50/30 transition-colors"
                    >
                      <td className="px-6 py-4 text-xs font-bold text-gray-400">
                        {idx + 1}
                      </td>
                      <td className="px-6 py-4 font-bold text-gray-900">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigateToSearch(student.name)}
                            className="hover:text-blue-600 hover:underline transition-colors text-left"
                          >
                            {student.name}
                          </button>
                          {student.wins > 1 && (
                            <span className="inline-flex items-center gap-0.5 text-xs font-black text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded-full animate-bounce">
                              <Trophy className="h-3 w-3" /> Multi-champ
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleStateChange(student.state)}
                          className="text-gray-600 font-bold hover:text-blue-600 hover:underline transition-colors"
                        >
                          {student.state}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-bold ${
                            student.gender === "Female"
                              ? "bg-pink-100 text-pink-600"
                              : student.gender === "Male"
                                ? "bg-blue-100 text-blue-600"
                                : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {student.gender || "N/A"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center font-black text-gray-800 text-base">
                        {student.wins}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          {student.years.map((year) => (
                            <button
                              key={year}
                              onClick={() => handleYearChange(year)}
                              className="text-xs font-bold px-2 py-1 rounded-lg bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-600 transition-all flex items-center gap-1"
                            >
                              <Trophy className="h-3 w-3 text-yellow-500" />
                              {year}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 2: 3-Time National Winners */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Award className="h-6 w-6 text-blue-600 mr-2" />
                  3-Time National Winners
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  Students who reached the National Top 56 in three consecutive years
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setThreeTimeCountdownOnly((prev) => !prev)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all self-start sm:self-auto ${
                    threeTimeCountdownOnly
                      ? "bg-blue-600 text-white shadow-md hover:bg-blue-700"
                      : "bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100"
                  }`}
                >
                  <Trophy className="h-4 w-4" />
                  Countdown Only
                </button>
                <select
                  value={threeTimeGenderFilter}
                  onChange={(e) => setThreeTimeGenderFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-medium text-gray-600"
                >
                  <option value="All">All Genders</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">
                      #
                    </th>
                    <th
                      className="px-6 py-4 text-xs font-bold text-gray-500 uppercase cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() => handleThreeTimeSort("name")}
                    >
                      <span className="flex items-center gap-1">
                        Student{" "}
                        {threeTimeSort.key === "name" ? (
                          threeTimeSort.dir === "asc" ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )
                        ) : (
                          <ChevronDown className="h-3 w-3 opacity-20" />
                        )}
                      </span>
                    </th>
                    <th
                      className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-center cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() => handleThreeTimeSort("state")}
                    >
                      <span className="flex items-center justify-center gap-1">
                        State{" "}
                        {threeTimeSort.key === "state" ? (
                          threeTimeSort.dir === "asc" ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )
                        ) : (
                          <ChevronDown className="h-3 w-3 opacity-20" />
                        )}
                      </span>
                    </th>
                    <th
                      className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-center cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() => handleThreeTimeSort("gender")}
                    >
                      <span className="flex items-center justify-center gap-1">
                        Gender{" "}
                        {threeTimeSort.key === "gender" ? (
                          threeTimeSort.dir === "asc" ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )
                        ) : (
                          <ChevronDown className="h-3 w-3 opacity-20" />
                        )}
                      </span>
                    </th>
                    <th
                      className="px-6 py-4 text-xs font-bold text-gray-500 uppercase cursor-pointer hover:text-blue-600 transition-colors"
                      onClick={() => handleThreeTimeSort("years")}
                    >
                      <span className="flex items-center gap-1">
                        Years (Ranks){" "}
                        {threeTimeSort.key === "years" ? (
                          threeTimeSort.dir === "asc" ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )
                        ) : (
                          <ChevronDown className="h-3 w-3 opacity-20" />
                        )}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {threeTimeQualifiers.map((student, idx) => (
                    <tr
                      key={`${student.name}-${student.state}-${idx}`}
                      className="hover:bg-blue-50/30 transition-colors"
                    >
                      <td className="px-6 py-4 text-xs font-bold text-gray-400">
                        {idx + 1}
                      </td>
                      <td className="px-6 py-4 font-bold text-gray-900">
                        <button
                          onClick={() => navigateToSearch(student.name)}
                          className="hover:text-blue-600 hover:underline transition-colors text-left"
                        >
                          {student.name}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleStateChange(student.state)}
                          className="text-gray-600 font-bold hover:text-blue-600 hover:underline transition-colors"
                        >
                          {student.state}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-bold ${
                            student.gender === "Female"
                              ? "bg-pink-100 text-pink-600"
                              : student.gender === "Male"
                                ? "bg-blue-100 text-blue-600"
                                : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {student.gender || "N/A"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          {student.years.map((year, yIdx) => (
                            <button
                              key={year}
                              onClick={() => handleYearChange(year)}
                              className="text-xs font-bold px-2 py-1 rounded-lg bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-600 transition-all"
                            >
                              {year} ({student.ranks[yIdx]})
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === "search" && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Search className="h-6 w-6 text-blue-600 mr-2" />
              Search Student Records
            </h3>
            <div className="relative max-w-2xl">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Type at least 2 characters to search student name..."
                className="pl-12 pr-4 py-4 w-full border border-gray-200 rounded-2xl text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                value={globalSearchQuery}
                onChange={(e) => setGlobalSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-900">
                {globalSearchQuery.trim().length < 2
                  ? "Search Results"
                  : `Found ${searchResults.length} records for "${globalSearchQuery}"`}
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">
                      Year
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">
                      Rank
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">
                      Student
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">
                      State
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">
                      Grade
                    </th>
                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">
                      Gender
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {searchResults.map((student, idx) => (
                    <tr
                      key={`${student.year}-${student.name}-${idx}`}
                      className="hover:bg-blue-50/30 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleYearChange(student.year)}
                          className="font-bold text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                        >
                          {student.year}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-sm font-bold ${
                            student.rank === "1"
                              ? "bg-yellow-100 text-yellow-700"
                              : student.rank === "2"
                                ? "bg-gray-200 text-gray-700"
                                : ["S", "Q", "C", "P"].includes(student.rank)
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-gray-50 text-gray-500"
                          }`}
                        >
                          {student.rank}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900">
                        <button
                          onClick={() => navigateToSearch(student.name)}
                          className="hover:text-blue-600 hover:underline transition-colors text-left"
                        >
                          {student.name}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleStateChange(student.state)}
                          className="text-gray-600 hover:text-blue-600 hover:underline transition-colors font-medium"
                        >
                          {student.state}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {student.grade || "N/A"}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-bold ${
                            student.gender === "Female"
                              ? "bg-pink-100 text-pink-600"
                              : student.gender === "Male"
                                ? "bg-blue-100 text-blue-600"
                                : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {student.gender}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {globalSearchQuery.trim().length >= 2 &&
                    searchResults.length === 0 && (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-6 py-12 text-center text-gray-500 italic"
                        >
                          No students found matching "{globalSearchQuery}"
                        </td>
                      </tr>
                    )}
                  {globalSearchQuery.trim().length < 2 && (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-6 py-12 text-center text-gray-400"
                      >
                        Please enter at least 2 characters to search...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MathcountsSection;
